import { type Static, Type } from "typebox";
import * as TypeCompiler from "typebox/compile";
import { readJsonScript } from "@/shared/bootstrap/bootContract.ts";
import {
  Nullable,
  ReadonlyArray,
  ReadonlyObject,
} from "@/shared/schema/schema.ts";

const ELEMENT_ID = "cvp-login-data";

// Rails renders every key on every login page, so each flow validates only the
// keys it needs rather than one schema of everything-optional.
const loginErrorSchema = ReadonlyObject({
  message: Type.String(),
  err_params: Type.Record(Type.String(), Nullable(Type.String())),
  support_email: Nullable(Type.String()),
});

const adminAsSchema = ReadonlyObject({
  users: ReadonlyArray(
    ReadonlyObject({
      id: Type.String(),
      name: Nullable(Type.String()),
      email: Type.String(),
    }),
  ),
});

const unknownUserSchema = ReadonlyObject({
  field: Type.String(),
  user_id: Type.String(),
  support_email: Nullable(Type.String()),
  federation_group_members: ReadonlyArray(
    ReadonlyObject({ subdomains: ReadonlyArray(Type.String()) }),
  ),
});

const domainSelectionSchema = ReadonlyObject({
  google_apps_domains: ReadonlyArray(ReadonlyObject({ domain: Type.String() })),
});

const passwordSchema = ReadonlyObject({
  password_error: Nullable(Type.String()),
  user_name: Nullable(Type.String()),
  user_birthday: Nullable(Type.String()),
  user_id: Nullable(Type.String()),
  reset_password_code: Nullable(Type.String()),
});

const changePasswordErrorSchema = ReadonlyObject({
  message: Nullable(Type.String()),
});

const loginErrorChecker = TypeCompiler.Compile(loginErrorSchema);
const adminAsChecker = TypeCompiler.Compile(adminAsSchema);
const unknownUserChecker = TypeCompiler.Compile(unknownUserSchema);
const domainSelectionChecker = TypeCompiler.Compile(domainSelectionSchema);
const passwordChecker = TypeCompiler.Compile(passwordSchema);
const changePasswordErrorChecker = TypeCompiler.Compile(
  changePasswordErrorSchema,
);

export type PasswordData = Static<typeof passwordSchema>;

export const readLoginErrorData = () =>
  readJsonScript(ELEMENT_ID, loginErrorChecker);

export const readAdminAsData = () => readJsonScript(ELEMENT_ID, adminAsChecker);

export const readUnknownUserData = () =>
  readJsonScript(ELEMENT_ID, unknownUserChecker);

export const readDomainSelectionData = () =>
  readJsonScript(ELEMENT_ID, domainSelectionChecker);

export const readPasswordData = () =>
  readJsonScript(ELEMENT_ID, passwordChecker);

export const readChangePasswordErrorData = () =>
  readJsonScript(ELEMENT_ID, changePasswordErrorChecker);
